import { ApiResponse } from 'apisauce';

import config from '../../../config';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { CharacterEnum } from '../../enums/character.enum';
import { PagedResponse, SimplePagedResponse } from '../../models/common-response-types';
import { Company } from '../../models/company';
import {
  AvailableIndexesResponse,
  BalanceSheet,
  CashFlow,
  CompanyLogoAndNameResponse,
  DividendPayerData,
  Earning,
  EpicMoversParamType,
  EpicMoversResponse,
  ETF,
  ETFDetailParams,
  ETFDetailResponse,
  ETFParams,
  GapperParamType,
  GapperResponse,
  HighGrowthCompanyData,
  Income,
  LiquidityAndSolvencyRatiosResponse,
  MarketCountryCode,
  NewStockData,
  NonWorkingDaysByCountryResponse,
  NonWorkingDaysResponse,
  PageableFilterRequest,
  PopularStock,
  ProfitabilityRatiosResponse,
  RatiosByTypeParamType,
  RatiosResponse,
  Sector,
  SectorDetail,
  SectorDetailParams,
  StatementApiResponse,
  StatementSearchParamType,
  StocksSymbolSet,
  ValuationRatiosResponse,
  Week52HighLowParamType,
  Week52HighLowResponse,
  Week52HighLowTopListParamType,
  Week52HighLowTopListResponse,
  MarketCapData,
  MarketCapsRequest,
} from '../../models/fundamental/types';
import { SortingType } from '../../models/pageable';
import { StockDescriptor } from '../../models/stock-descriptor';
import { SymbolKeyStat } from '../../models/symbol-key-stat';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_LIST_SIZE,
  DEFAULT_SEARCH_STOCK_LIMIT,
  DEFAULT_SECTOR_PAGE,
  GET_FIRST_ITEM_SIZE,
} from '../../store/fundamental/constants';
import { SearchEarningsPayload } from '../../store/fundamental/types';
import { NullableString } from '../../util/types';
import { BaseRequestService } from '../BaseRequest.service';
import { DOT } from '../cache/constants';
import { CacheName, HttpMethods } from '../cache/types';
import { DynamicFilterType } from '../filter/types';
import { IFundamentalService } from '../types';


const API_URLS = {
  searchCompanies: '/companies/search',
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  earnings: '/earnings',
  earningsByInterval: '/earnings/by-interval',
  earningsSearch: '/earnings/search',
  keyStats: '/key_stats',
  ETFKeyStats: '/key-stats',
  getCompany: '/companies',
  getCompanyLogoAndName: '/companies/logo',
  getCompanyLogoMD5: '/companies/logo-md5',
  popularStocks: '/symbols/popular',
  newStocks: '/symbols/new',
  dividendPayers: '/symbols/dividend-payers',
  highGrowthCompanies: '/companies/high-growth',
  sectors: '/sectors',
  sector: '/symbols/sector',
  getMarketIndexes: '/market-index/latest/search',
  epicMovers: '/symbols/top-gainers-losers',
  marketCaps: '/symbols/market-caps',
  epicMoversCustom: '/symbols/top-gainers-losers/custom',
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  symbols: '/symbols?',
  etfs: '/symbols',
  statements: '/statements',
  statementsBalanceSheet: '/statements/balance-sheet',
  statementsCashFlow: '/statements/cash_flow',
  statementsIncome: '/statements/income',
  availableIndexes: '/symbols/available-indexes',
  gappers: '/symbols/gappers',
  week52HighLow: '/symbols/week52-high-lows',
  week52HighLowTopList: '/symbols/week52-top-n-high-lows',
  ratiosByType: '/ratios/by-type', // All ratios[valuation, liquidityAndSolvency, profitability]
  ratiosLiquidityAndSolvency: '/ratios/by-type/liquidity-and-solvency',
  ratiosProfitability: '/ratios/by-type/profitability',
  ratiosValuation: '/ratios/by-type/valuation',
  etfsInfo: '/etfs/info',
  etfsHolders: 'etfs/holders',
  etfsWeightnings: '/etfs/weighting/sector',
  nonWorkingDaysWithMarketHours: '/non-working-days/with-market-hours',
  nonWorkingDaysWithMarketHoursMultiple: '/non-working-days/with-market-hours/multiple',
};

export class FundamentalService extends BaseRequestService implements IFundamentalService {
  private static instance: FundamentalService;

  protected getBaseUrl(): string {
    return config.fundamental.url;
  }

  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    super();
  }

  static getInstance(): FundamentalService {
    this.instance ??= new FundamentalService();
    return this.instance;
  }

  private getItems<T>(url: string, urlForInterval: string, allSymbols: string[], startDate?: string, endDate?: string) {
    const symbols = allSymbols?.reduce((prev, curr) => `${prev},${curr}`, '');
    if (startDate !== undefined && endDate !== undefined) {
      return this.api.get<T[]>(urlForInterval, { symbols, startDate, endDate });
    }
    return this.api.get<T[]>(url, { symbols });
  }

  /**
   * @param query text search parameter
   * @param params dynamic filter search parameter
   * @param limit expected result count
   * @param symbolSet Two cases are possible, as follows:
   *   Case 1
   *   If empty or `NONE` - full list of symbols is searched
   *
   *   Case 2
   *   If `RETAIL` - 10n symbol universe is searched
   * 
   *   Case 3
   *   If `ACE` - ACE symbol universe is searched
   *
   *   For example if searching 'com':
   *     Case 1 - 5934 symbols are returned (2022-09-23 12pm EEST)
   *     Case 2 - 870 symbols are returned (2022-09-23 12pm EEST)
   */
  public searchCompanies(
    query: string,
    params: DynamicFilterType | undefined,
    limit = 16,
    symbolSet: StocksSymbolSet = StocksSymbolSet.ACE,
  ) {
    const APIPath = API_URLS.searchCompanies;
    const url = this.getBaseUrl() + APIPath;
    const issueTypeKey = `filters${DOT}eq${DOT}issueType`;
    const cacheConditions: Record<string, NullableString[]> = {};

    cacheConditions.limit = [ String(DEFAULT_SEARCH_STOCK_LIMIT) ];
    cacheConditions[issueTypeKey] = [ 'cs', 'et', undefined ];
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.searchCompanies,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveMinutes,
      [ 'text', issueTypeKey, 'limit' ],
      cacheConditions,
    );

    return this.api.get<StockDescriptor[]>(APIPath, {
      text: query,
      searchFields: 'symbol,companyName',
      projections: 'symbol,companyName,countryDesignation,stockExchangeCode',
      symbolSet,
      limit,
      filters: params ? JSON.stringify(params) : null,
    });
  }


  public searchEarnings(params?: SearchEarningsPayload) {
    const DEFAULT_EARNING_SEARCH_SORT_FIELD = 'date';
    const { symbols, startDate, endDate } = params ?? {};

    const url = FundamentalService.preparePageableRequestURL(
      API_URLS.earningsSearch,
      DEFAULT_EARNING_SEARCH_SORT_FIELD,
      {
        ...params,
        sortAscending: true,
      },
    );

    /**
     * @deprecated
     * Please use DynamicFilterBuilder(services/filter/dynamic-filter-builder) for the common filtering solution
     */
    const filters = {
      in: symbols && symbols.length > 0 ? symbols.join(',') : undefined,
      lte: endDate ? { date: endDate } : undefined,
      gte: startDate ? { date: startDate } : undefined,
    };

    // The shared calendar renders the report status, the EPS surprise and the revenue figures,
    // so they have to be projected - the endpoint omits anything not listed here.
    const projections = [
      'date',
      'time',
      'eps',
      'epsPrior',
      'epsSurprise',
      'epsSurprisePercent',
      'epsEst',
      'symbol',
      'currency',
      'reportStatus',
      'revenue',
      'revenueEst',
      'revenueSurprise',
      'revenueSurprisePercent',
      'period',
      'periodYear',
    ];
    const uri = `${url}&filters=${JSON.stringify(filters)}&projections=${projections}`;
    return this.api.get<PagedResponse<Earning>>(encodeURI(uri), { symbolSet: StocksSymbolSet.ACE });
  }

  public getKeyStats(symbols: string) {
    const APIPath = API_URLS.keyStats;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.keyStatsBySymbols,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneHour,
      [ 'symbols' ],
    );
    return this.api.get<SymbolKeyStat[]>(APIPath, { symbols });
  }

  public getCompany(symbols: string) {
    const APIPath = API_URLS.getCompany;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.companyBySymbols,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneHour,
      [ 'symbols' ],
    );
    return this.api.get<Company[]>(APIPath, { symbols });
  }


  public getCompanyLogoAndName(symbols: string) {
    const APIPath = API_URLS.getCompanyLogoAndName;
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${APIPath}`;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.companyLogoAndNameBySymbols,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveMinutes,
      [ 'symbols' ],
    );

    const params = { symbols, projections: 'symbol,url,name,countryDesignation,stockExchangeCode' };

    return this.api.get<CompanyLogoAndNameResponse[]>(APIPath, params);
  }

  public getPopularStocks(constrained: boolean = true, count: number = 0) {
    const APIPath = API_URLS.popularStocks;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.popularStocks,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveMinutes,
      [ 'count', 'constrained' ],
    );
    return this.api.get<PopularStock[]>(APIPath, {
      constrained,
      count,
    });
  }

  public getNewStocks(symbolSet: StocksSymbolSet = StocksSymbolSet.ACE) {
    return this.api.get<NewStockData[]>(API_URLS.newStocks, { symbolSet });
  }

  public getDividendPayers(request?: PageableFilterRequest) {
    const DEFAULT_DIVIDEND_PAYERS_SORT_FIELD = 'dividendYield';
    const APIPath = FundamentalService.preparePageableRequestURL(
      API_URLS.dividendPayers,
      DEFAULT_DIVIDEND_PAYERS_SORT_FIELD,
      request,
    );
    const params = {
      projections: 'close, currency, currentDividendPayment, dividendRate, dividendYield, enabled, exchange, marginable, marketCap, order, shortable, symbology, tape, type, volume, symbol, url, name',
      symbolSet: StocksSymbolSet.ACE,
    };
    return this.api.get<DividendPayerData[]>(APIPath, params);
  }

  public getHighGrowthCompanies(request?: PageableFilterRequest) {
    const DEFAULT_HIGH_GROWTH_SORT_FIELD = 'growthIndicator';
    const APIPath = FundamentalService.preparePageableRequestURL(
      API_URLS.highGrowthCompanies,
      DEFAULT_HIGH_GROWTH_SORT_FIELD,
      request,
    );
    return this.api.get<HighGrowthCompanyData[]>(APIPath, { symbolSet: StocksSymbolSet.ACE });
  }

  private static preparePageableRequestURL(
    url: string,
    defaultSortingField: string,
    request?: PageableFilterRequest,
  ): string {
    const DEFAULT_FETCH_SYMBOL_UNIVERSE_SYMBOLS = true;
    const DEFAULT_SORTING_TYPE = SortingType.DESC;
    const DEFAULT_PAGE_SIZE = 15;

    if (!request) {
      return `${url}?page=${DEFAULT_PAGE}`
        + `&isSymbolUniverseSymbols=${DEFAULT_FETCH_SYMBOL_UNIVERSE_SYMBOLS}`
        + `&size=${DEFAULT_PAGE_SIZE}`
        + `&sort=${defaultSortingField},${DEFAULT_SORTING_TYPE}`;
    }

    return `${url}?page=${request.page || DEFAULT_PAGE}`
      + `&size=${request.size || DEFAULT_PAGE_SIZE}`
      + `&isSymbolUniverseSymbols=${request.isSymbolUniverseSymbols || DEFAULT_FETCH_SYMBOL_UNIVERSE_SYMBOLS}`
      + `&sort=${request.sort || defaultSortingField},${request.sortAscending ? SortingType.ASC : DEFAULT_SORTING_TYPE}`;
  }

  public getSectors() {
    const APIPath = API_URLS.sectors;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(url, CacheName.sectors, HttpMethods.GET, PredefinedMilisecondsTypeEnum.oneHour);
    return this.api.get<Sector[]>(APIPath);
  }

  public getNonWorkingDaysAndMarketTime(country: MarketCountryCode) {
    const APIPath = `${API_URLS.nonWorkingDaysWithMarketHours}`;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.nonWorkingDaysAndWorkingTime,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
      [ 'country' ],
    );

    return this.api.get<NonWorkingDaysResponse>(APIPath, { country });
  }

  public getNonWorkingDaysAndMarketTimeMultiple(countries: MarketCountryCode[]) {
    const APIPath = API_URLS.nonWorkingDaysWithMarketHoursMultiple;
    const countriesQuery = countries.map(encodeURIComponent).join(CharacterEnum.Comma);
    const apiPathWithQuery = `${APIPath}?countries=${countriesQuery}`;
    const url = this.getBaseUrl() + apiPathWithQuery;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.nonWorkingDaysAndWorkingTime,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
    );

    return this.api.get<NonWorkingDaysByCountryResponse>(apiPathWithQuery);
  }

  public getSectorDetail(params: SectorDetailParams) {
    const APIPath = API_URLS.sector;
    const url = this.getBaseUrl() + APIPath;
    const cacheConditions: Record<string, NullableString[]> = {};
    cacheConditions.size = [ String(DEFAULT_PAGE_LIST_SIZE) ];
    cacheConditions.page = [ String(DEFAULT_SECTOR_PAGE) ];
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.symbolsBySector,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneHour,
      [ 'sectorId', 'page', 'size' ],
      cacheConditions,
    );
    return this.api.get<SectorDetail>(APIPath, { ...params, symbolSet: StocksSymbolSet.ACE });
  }

  public getETFs(params: ETFParams) {
    const APIPath = API_URLS.etfs;
    const url = this.getBaseUrl() + APIPath;
    const typeKey = `filters${DOT}eq${DOT}type`;
    const cacheConditions: Record<string, NullableString[]> = {};
    cacheConditions.page = [ String(DEFAULT_PAGE) ];
    cacheConditions.size = [ String(GET_FIRST_ITEM_SIZE) ];
    cacheConditions[typeKey] = [ 'et' ];

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.etfs,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneHour,
      [ 'page', typeKey, 'size' ],
      cacheConditions,
    );
    return this.api.get<PagedResponse<ETF>>(APIPath, params);
  }





  public getStatements(params: StatementSearchParamType): Promise<ApiResponse<StatementApiResponse>> {
    const APIPath = API_URLS.statements;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.statements,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveHours,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ],
    );

    return this.api.get<StatementApiResponse>(APIPath, params);
  }

  public getStatementsCashFlow(params: StatementSearchParamType): Promise<ApiResponse<CashFlow[]>> {
    const APIPath = API_URLS.statementsCashFlow;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.statementsCashFlow,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveHours,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ],
    );

    return this.api.get<CashFlow[]>(APIPath, params);
  }

  public getStatementsBalanceSheet(params: StatementSearchParamType): Promise<ApiResponse<BalanceSheet[]>> {
    const APIPath = API_URLS.statementsBalanceSheet;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.statementsBalanceSheet,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveHours,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ],
    );

    return this.api.get<BalanceSheet[]>(APIPath, params);
  }

  public getStatementsIncome(params: StatementSearchParamType): Promise<ApiResponse<Income[]>> {
    const APIPath = API_URLS.statementsIncome;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.statementsIncome,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveHours,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ],
    );

    return this.api.get<Income[]>(APIPath, params);
  }

  public getGappers(params: GapperParamType): Promise<ApiResponse<SimplePagedResponse<GapperResponse>>> {
    const APIPath = API_URLS.gappers;
    const url = this.getBaseUrl() + APIPath;
    const cacheConditions: Record<string, NullableString[]> = {};
    cacheConditions.size = [ String(DEFAULT_PAGE_LIST_SIZE) ];
    cacheConditions.page = [ String(DEFAULT_PAGE) ];

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.gappers,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveHours,
      [ 'abovePreviousClose', 'symbolSet', 'page', 'size' ],
      cacheConditions,
    );

    return this.api.get<SimplePagedResponse<GapperResponse>>(APIPath, params);
  }

  public getWeek52HighLow(
    params: Week52HighLowParamType,
  ): Promise<ApiResponse<SimplePagedResponse<Week52HighLowResponse>>> {
    const APIPath = API_URLS.week52HighLow;
    const url = this.getBaseUrl() + APIPath;
    const cacheConditions: Record<string, NullableString[]> = {};
    cacheConditions.size = [ String(DEFAULT_PAGE_LIST_SIZE) ];
    cacheConditions.page = [ String(DEFAULT_PAGE) ];

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.week52HighLow,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fiveHours,
      [ 'changeStatus', 'symbolSet', 'page', 'size' ],
      cacheConditions,
    );

    return this.api.get<SimplePagedResponse<Week52HighLowResponse>>(APIPath, params);
  }

  public getWeek52HighLowTopList(
    params: Week52HighLowTopListParamType,
  ): Promise<ApiResponse<Week52HighLowTopListResponse>> {
    const APIPath = API_URLS.week52HighLowTopList;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.week52HighLowTopList,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
      [],
    );

    return this.api.get<Week52HighLowTopListResponse>(APIPath, params);
  }

  public getAllRatios(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<RatiosResponse>> {
    const APIPath = API_URLS.ratiosByType;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.ratiosByType,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ], // startDate and endDate will be static on the UI side thats why they are defined here as cacheKeys
    );

    return this.api.get<RatiosResponse>(APIPath, params);
  }

  public getRatiosValuation(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<ValuationRatiosResponse>> {
    const APIPath = API_URLS.ratiosValuation;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.ratiosValuation,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ], // startDate and endDate will be static on the UI side thats why they are defined here as cacheKeys
    );

    return this.api.get<ValuationRatiosResponse>(APIPath, params);
  }

  public getRatiosProfitability(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<ProfitabilityRatiosResponse>> {
    const APIPath = API_URLS.ratiosProfitability;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.ratiosProfitability,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ], // startDate and endDate will be static on the UI side thats why they are defined here as cacheKeys
    );

    return this.api.get<ProfitabilityRatiosResponse>(APIPath, params);
  }

  public getRatiosLiquidityAndSolvency(
    params: RatiosByTypeParamType,
  ): Promise<ApiResponse<LiquidityAndSolvencyRatiosResponse>> {
    const APIPath = API_URLS.ratiosLiquidityAndSolvency;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.ratiosLiquidityAndSolvency,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
      [ 'symbol', 'periodType', 'startDate', 'endDate' ], // startDate and endDate will be static on the UI side thats why they are defined here as cacheKeys
    );

    return this.api.get<LiquidityAndSolvencyRatiosResponse>(APIPath, params);
  }

  public getAvailableIndexes(): Promise<ApiResponse<AvailableIndexesResponse[]>> {
    const APIPath = API_URLS.availableIndexes;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.availableIndexes,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneDay,
    );

    return this.api.get<AvailableIndexesResponse[]>(url);
  }

  public getETFDetails(params: ETFDetailParams) {
    return this.api.get<PagedResponse<ETFDetailResponse>>(API_URLS.ETFKeyStats, params);
  }

  public getMarketCaps(
    params: MarketCapsRequest,
  ): Promise<ApiResponse<SimplePagedResponse<MarketCapData>>> {
    const APIPath = API_URLS.marketCaps;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.marketCaps,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneMinute,
      ['page', 'size', 'sort', 'filters.eq.category'],
    );

    return this.api.get<SimplePagedResponse<MarketCapData>>(APIPath, params);
  }

  public getEpicMovers(
    params: EpicMoversParamType,
  ): Promise<ApiResponse<EpicMoversResponse>> {
    const APIPath = API_URLS.epicMovers;
    const url = this.getBaseUrl() + APIPath;

    this.addOrUpdateCacheDefinition(
      url,
      CacheName.EpicMovers,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.fifteenMinutes,
      [ 'range' ],
    );

    return this.api.get<EpicMoversResponse>(APIPath, params);
  }



  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */

  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */

  public getMarketIndexes(symbols: string[]): Promise<ApiResponse<string[], string[]>> {
    const url = API_URLS.getMarketIndexes;
    const params = {
      symbols: symbols.join(','), // Convert the symbols array to a comma-separated string
    };
    return this.api.get<string[]>(url, params);
  }


}
